import { createContext, useContext, useMemo, useReducer } from 'react'

export const TYPES = {
  SET_READONLY_FLAGS: 'SET_READONLY_FLAGS',
  SET_FLAGS: 'SET_FLAGS',
  SET_LOADING: 'SET_LOADING',
  SET_MODAL: 'SET_MODAL',
  SET_ACTION: 'SET_ACTION',
  RESET_ACTION: 'RESET_ACTION',
  SET_SUBMIT_FAILED: 'SET_SUBMIT_FAILED',
  SET_ACTION_FAILED: 'SET_ACTION_FAILED',
  SET_NEXT_ACTION_PC_FAILED: 'SET_NEXT_ACTION_PC_FAILED',
}

const UiContext = createContext(null)

const initialUi = {
  // flags
  isReadonly: false,
  isNonCompliant: false,
  isArchived: false,
  isRenderReadOnlySection: false,
  isNBIOR: false,
  isUFLPA: false,

  // loading + failures (internal)
  loading: {
    fetch: true,
    fetchFailed: false,
    save: false,
    saveFailed: false,
    submit: false,
    submitFailed: false,
    action: false,
    actionFailed: false,
    nextActionPC: false,
    nextActionPCFailed: false,
  },

  // top-level mirrors that components read
  fetchFailed: false,
  saveFailed: false,
  submitFailed: false,
  actionFailed: false,
  nextActionPCFailed: false,

  // modals / dialogs
  modals: {
    prefill: true,
    confirmation: false,
    ctvContacts: false,
    ctpContacts: false,
    email: false,
    submitValidation: false,
    actionConfirmation: false,
    productCapabilities: { isVisible: false, isReadOnly: true },
    capabilityAudit: { isVisible: false, isReadOnly: true },
  },

  // actions
  userAction: '',
  userComments: '',
  isSaveAndCloseClicked: false,
}

function uiReducer(state, action) {
  switch (action.type) {
    case TYPES.SET_READONLY_FLAGS:
      return { ...state, isReadonly: !!action.payload }
    case TYPES.SET_FLAGS:
      return { ...state, ...action.payload }
    case TYPES.SET_LOADING: {
      const nextLoading = { ...state.loading, ...(action.payload || {}) }
      const p = action.payload || {}

      return {
        ...state,
        loading: nextLoading,

        // keep the top-level failure flags in sync whenever they are provided
        ...(Object.prototype.hasOwnProperty.call(p, 'fetchFailed') && {
          fetchFailed: !!p.fetchFailed,
        }),
        ...(Object.prototype.hasOwnProperty.call(p, 'saveFailed') && {
          saveFailed: !!p.saveFailed,
        }),
        ...(Object.prototype.hasOwnProperty.call(p, 'submitFailed') && {
          submitFailed: !!p.submitFailed,
        }),
        ...(Object.prototype.hasOwnProperty.call(p, 'actionFailed') && {
          actionFailed: !!p.actionFailed,
        }),
        ...(Object.prototype.hasOwnProperty.call(p, 'nextActionPCFailed') && {
          nextActionPCFailed: !!p.nextActionPCFailed,
        }),
      }
    }
    case TYPES.SET_MODAL:
      return { ...state, modals: { ...state.modals, ...action.payload } }
    case TYPES.SET_ACTION:
      return { ...state, ...action.payload }
    case TYPES.RESET_ACTION:
      return { ...state, userAction: '', userComments: '' }
    case TYPES.SET_SUBMIT_FAILED:
      return { ...state, submitFailed: !!action.payload }
    case TYPES.SET_ACTION_FAILED:
      return { ...state, actionFailed: !!action.payload }
    case TYPES.SET_NEXT_ACTION_PC_FAILED:
      return { ...state, nextActionPCFailed: !!action.payload }
    default:
      return state
  }
}

export function UiProvider({ children }) {
  const [state, dispatch] = useReducer(uiReducer, initialUi)
  const value = useMemo(() => ({ state, dispatch }), [state])
  return <UiContext.Provider value={value}>{children}</UiContext.Provider>
}

export function useUi() {
  const ctx = useContext(UiContext)
  if (!ctx) throw new Error('useUi must be used within <UiProvider>')
  return ctx
}
